/**
 * Location Finder Module - Xử lý việc tìm kiếm và hiển thị danh sách cửa hàng
 * Hỗ trợ layout 2 cột: danh sách cửa hàng bên trái, bản đồ bên phải
 */
class LocationFinder {
  constructor() {
    this.loadEmbedmapContainer = document.getElementById("load-embedmap");
    this.searchInput = document.getElementById("search");
    this.storeListContainer = document.getElementById("store-list");
    this.allStores = [];
    this.selectedRegionId = 0;
    this.selectedAttributeIds = new Set();

    const locationPage = document.getElementById("location-page");
    this.translations = {
      noResultsMessage:
        locationPage?.getAttribute("data-no-results-message") ||
        "Không tìm thấy cửa hàng phù hợp",
      mapLoading:
        locationPage?.getAttribute("data-map-loading") || "Đang tải bản đồ...",
    };

    if (this.loadEmbedmapContainer) {
      this.init();
    }
  }

  init() {
    this.storeAllStores();
    this.bindEvents();
    this.applyFilters();
  }

  bindEvents() {
    document.addEventListener("click", (e) => {
      const storeDiv = e.target.closest(".store-item");
      if (storeDiv) {
        e.preventDefault();
        const storeId = storeDiv.getAttribute("data-store-id");
        if (storeId) {
          this.loadStoreEmbedmap(storeId, storeDiv);
        }
      }

      const regionTab = e.target.closest(".location-region-tab");
      if (regionTab) {
        e.preventDefault();
        this.handleRegionTabClick(regionTab);
      }

      const attributeFilter = e.target.closest(".location-attribute-filter");
      if (attributeFilter) {
        e.preventDefault();
        this.handleAttributeFilterClick(attributeFilter);
      }
    });

    if (this.searchInput) {
      this.searchInput.addEventListener("input", (e) => {
        this.applyFilters(e.target.value);
      });
    }
  }

  handleRegionTabClick(tabElement) {
    this.selectedRegionId = parseInt(tabElement.getAttribute("data-region-id") || "0", 10);

    document.querySelectorAll(".location-region-tab").forEach((tab) => {
      tab.classList.remove("bg-gray-900", "text-white");
      tab.classList.add("text-gray-700", "hover:bg-gray-50");
    });

    tabElement.classList.add("bg-gray-900", "text-white");
    tabElement.classList.remove("text-gray-700", "hover:bg-gray-50");

    this.applyFilters(this.searchInput?.value || "");
  }

  handleAttributeFilterClick(filterElement) {
    const attributeId = parseInt(
      filterElement.getAttribute("data-attribute-id") || "0",
      10
    );

    if (attributeId <= 0) {
      return;
    }

    if (this.selectedAttributeIds.has(attributeId)) {
      this.selectedAttributeIds.delete(attributeId);
      filterElement.classList.remove("bg-gray-900", "text-white");
      filterElement.classList.add("text-gray-700", "hover:bg-gray-50");
    } else {
      this.selectedAttributeIds.add(attributeId);
      filterElement.classList.add("bg-gray-900", "text-white");
      filterElement.classList.remove("text-gray-700", "hover:bg-gray-50");
    }

    this.applyFilters(this.searchInput?.value || "");
  }

  loadStoreEmbedmap(storeId, storeDiv) {
    if (!this.loadEmbedmapContainer) {
      console.warn("⚠️ load-embedmap container not found");
      return;
    }

    this.highlightSelectedStore(storeDiv);

    const embedmap = storeDiv.getAttribute("data-embedmap");

    if (embedmap && embedmap.trim() !== "") {
      this.showLoadingState();

      setTimeout(() => {
        const responsiveEmbedmap = this.makeEmbedmapResponsive(embedmap);
        this.loadEmbedmapContainer.innerHTML = responsiveEmbedmap;
      }, 300);
    } else {
      console.warn("⚠️ No embedmap found for store:", storeId);
      this.showEmptyMap();
    }
  }

  highlightSelectedStore(selectedStoreDiv) {
    const allStoreItems = document.querySelectorAll(".store-item");
    allStoreItems.forEach((item) => {
      item.classList.remove("bg-gray-100");
    });

    selectedStoreDiv.classList.add("bg-gray-100");
  }

  loadFirstStore() {
    const visibleStoreItem = Array.from(
      document.querySelectorAll(".store-item")
    ).find((item) => item.style.display !== "none");

    if (visibleStoreItem) {
      const storeId = visibleStoreItem.getAttribute("data-store-id");
      if (storeId) {
        this.loadStoreEmbedmap(storeId, visibleStoreItem);
      }
    }
  }

  storeAllStores() {
    const storeItems = document.querySelectorAll(".store-item");
    this.allStores = Array.from(storeItems).map((item) => ({
      element: item,
      id: item.getAttribute("data-store-id"),
      regionId: parseInt(item.getAttribute("data-region-id") || "0", 10),
      attributeDetail: this.parseAttributeDetail(
        item.getAttribute("data-attribute-detail")
      ),
      name:
        item.querySelector(".font-medium.text-gray-900")?.textContent?.trim() ||
        "",
      address:
        item.querySelector(".text-xs.text-gray-500")?.textContent?.trim() || "",
    }));
  }

  parseAttributeDetail(rawValue) {
    if (!rawValue) {
      return [];
    }

    try {
      const parsed = JSON.parse(rawValue);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  storeHasAttributeValue(store, attributeId) {
    const match = store.attributeDetail.find(
      (item) => parseInt(item.id, 10) === attributeId
    );

    if (!match) {
      return false;
    }

    const value = String(match.value ?? "").trim().toLowerCase();
    return value === "1" || value === "true" || value === "yes";
  }

  removeVietnameseAccents(str) {
    if (!str) return "";

    return str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  }

  applyFilters(searchTerm = "") {
    const term = searchTerm.toLowerCase().trim();
    const termWithoutAccents = this.removeVietnameseAccents(term);

    let filteredStores = this.allStores;

    if (this.selectedRegionId > 0) {
      filteredStores = filteredStores.filter(
        (store) => store.regionId === this.selectedRegionId
      );
    }

    if (this.selectedAttributeIds.size > 0) {
      filteredStores = filteredStores.filter((store) => {
        for (const attributeId of this.selectedAttributeIds) {
          if (!this.storeHasAttributeValue(store, attributeId)) {
            return false;
          }
        }
        return true;
      });
    }

    if (term) {
      filteredStores = filteredStores.filter((store) => {
        const name = store.name.toLowerCase();
        const address = store.address.toLowerCase();
        const nameWithoutAccents = this.removeVietnameseAccents(store.name);
        const addressWithoutAccents = this.removeVietnameseAccents(
          store.address
        );

        const nameMatch = name.includes(term);
        const addressMatch = address.includes(term);
        const nameMatchNoAccent =
          nameWithoutAccents.includes(termWithoutAccents);
        const addressMatchNoAccent =
          addressWithoutAccents.includes(termWithoutAccents);

        return (
          nameMatch || addressMatch || nameMatchNoAccent || addressMatchNoAccent
        );
      });
    }

    this.displayFilteredStores(filteredStores);
  }

  displayFilteredStores(filteredStores) {
    this.allStores.forEach((store) => {
      store.element.style.display = "none";
    });

    filteredStores.forEach((store) => {
      store.element.style.display = "block";
    });

    if (filteredStores.length > 0) {
      const firstFilteredStore = filteredStores[0];
      this.loadStoreEmbedmap(firstFilteredStore.id, firstFilteredStore.element);
    } else {
      this.showEmptySearchResults();
    }
  }

  showEmptySearchResults() {
    if (this.loadEmbedmapContainer) {
      this.loadEmbedmapContainer.innerHTML = `
                <div class="absolute inset-0 flex items-center justify-center bg-gray-200 rounded-lg">
                    <div class="text-center">
                        <svg class="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                        </svg>
                        <p class="text-gray-500">${this.translations.noResultsMessage}</p>
                    </div>
                </div>
            `;
    }
  }

  showLoadingState() {
    if (this.loadEmbedmapContainer) {
      this.loadEmbedmapContainer.innerHTML = `
        <div class="absolute inset-0 flex items-center justify-center bg-gray-200 rounded-lg">
          <div class="text-center">
            <svg class="animate-spin w-8 h-8 text-blue-600 mx-auto mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p class="text-gray-600">${this.translations.mapLoading}</p>
          </div>
        </div>
      `;
    }
  }

  showEmptyMap() {
    if (this.loadEmbedmapContainer) {
      this.loadEmbedmapContainer.innerHTML = `
        <div class="absolute inset-0 flex items-center justify-center bg-gray-200 rounded-lg">
          <div class="text-center">
            <svg class="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
            </svg>
            <p class="text-gray-500">Không có bản đồ cho cửa hàng này</p>
          </div>
        </div>
      `;
    }
  }

  makeEmbedmapResponsive(embedmap) {
    const wrapper = `
      <div class="responsive-iframe-wrapper">
        ${embedmap}
      </div>
    `;

    return wrapper.replace(
      /(width|height)=["']?\d+["']?/gi,
      (match, attr) => `${attr}="100%"`
    );
  }
}

export default LocationFinder;
